import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MessageService, ConfirmationService } from 'primeng/api'
import { Component, OnInit } from '@angular/core'

import { ICutoffConfiguration, ICreateCutoffConfigurationDto, IUpdateCutoffConfigurationDto } from '../../data/interfaces/wallet-config.interface'
import { WALLET_CONFIG_COLUMNS, WALLET_CONFIG_ACTIONS, COMMON_CRON_EXPRESSIONS } from '../../data/constants/wallet-config.constants'
import { ApiCustomerService } from '@modules/private/customer/data/services/api/api-customer.service'
import { ApiWalletConfigService } from '../../data/services/api/api-wallet-config.service'
import { WalletConfigService } from '../../data/services/view/wallet-config.service'

@Component({
  selector: 'app-wallet-config-list',
  templateUrl: './wallet-config-list.component.html',
  styleUrls: ['./wallet-config-list.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class WalletConfigListComponent implements OnInit {
  columns = WALLET_CONFIG_COLUMNS
  actions = WALLET_CONFIG_ACTIONS

  /**
   * Esta tabla enseña SOLO los cortes de cartera.
   *
   * Desde F-098 el mismo historial guarda CENTRAL, el cuadre, la mora, el avance y la hoja de carga.
   * Sin este filtro, una pestaña llamada «Cortes» acababa listando seis reportes que no son cortes y
   * que no se generan ni se configuran desde aquí. El historial completo vive en Reportería.
   *
   *! El `null` no sobra: las entradas anteriores a F-098 no traen `reportType`, y todas ellas eran
   *! cortes de cartera. Sin él desaparecería de esta pestaña todo el histórico previo.
   */
  readonly cortesOnly = { reportType: { value: ['corte-cartera', null], matchMode: 'in' } }

  // Dialogs
  showGenerateDialog = false
  showStatsDialog = false
  showConfigurationsDialog = false
  showConfigFormDialog = false // Nuevo dialog para formulario

  // Forms
  generateForm!: FormGroup
  configForm!: FormGroup // Nuevo formulario para configuraciones

  // Data
  statistics: any = {}

  // Configuraciones
  primaryConfiguration: ICutoffConfiguration | null = null
  additionalConfigurations: ICutoffConfiguration[] = []

  // Form data
  configFormMode: 'CREATE' | 'EDIT' = 'CREATE'
  editingConfiguration: ICutoffConfiguration | null = null
  cronPresets = COMMON_CRON_EXPRESSIONS
  availableClients: any[] = [] // Se cargaría desde el backend
  showCustomCron = false
  canCreatePrimary = true // Se determinaría según permisos del usuario

  // Loading states
  loadingStats = false
  loadingConfigurations = false
  loadingGenerate = false
  loadingConfigForm = false // Nuevo loading state
  loadingClients = false // Loading state para clientes

  // Utility properties
  today = new Date()
  downloadingReports: Set<string> = new Set() // Para rastrear descargas en progreso

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public apiWalletConfigService: ApiWalletConfigService,
    public walletConfigService: WalletConfigService,
    private apiCustomerService: ApiCustomerService,
  ) {
    this.initForms()
  }

  ngOnInit(): void {
    this.loadStatistics()
    this.loadAvailableClients()
  }

  initForms(): void {
    this.generateForm = this.fb.group({
      cutoffDate: ['', Validators.required],
      clientIds: [[]],
      comments: [''],
    })

    // Nuevo formulario para configuraciones
    this.configForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      cronExpression: ['', [Validators.required, this.cronValidator]],
      cronPreset: [''], // Campo auxiliar para presets
      recipients: ['', [Validators.required, this.recipientsValidator]],
      clientIds: [[]],
      isActive: [true],
      isPrimary: [false],
    })
  }

  // Validador personalizado para expresiones cron
  cronValidator(control: any) {
    const cronRegex = /^(\*|[0-5]?\d)\s+(\*|[01]?\d|2[0-3])\s+(\*|[012]?\d|3[01]|L)\s+(\*|[0-9]|1[0-2])\s+(\*|[0-6])$/
    return cronRegex.test(control.value) ? null : { pattern: true }
  }

  // Validador personalizado para destinatarios
  recipientsValidator(control: any) {
    if (!control.value) return { required: true }

    const emails = control.value.split(',').map((email: string) => email.trim())
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    // Validar formato de emails
    if (!emails.every((email: string) => emailRegex.test(email))) return { pattern: true }

    // Validar máximo de destinatarios
    if (emails.length > 10) return { maxRecipients: true }

    return null
  }

  mainActions(flow: string): void {
    switch (flow) {
      case 'GENERATE':
        this.showGenerateDialog = true
        break
      case 'STATS':
        this.showStatsDialog = true
        break
      case 'CONFIGURATIONS': // Cambio de 'RECENT'
        this.loadConfigurations()
        this.showConfigurationsDialog = true
        break
      default:
        console.log(flow)
        break
    }
  }

  generateCorte(): void {
    if (this.generateForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos correctamente',
      })
      return
    }

    this.loadingGenerate = true
    const formData = this.generateForm.value

    const requestData = {
      cutoffDate: formData.cutoffDate,
      clientIds: formData.clientIds || [],
      comments: formData.comments || '',
    }

    this.apiWalletConfigService.generateCorte(requestData).subscribe({
      next: (response: Blob) => {
        this.loadingGenerate = false

        if (response instanceof Blob && response.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          const blobUrl = window.URL.createObjectURL(response)
          const a = document.createElement('a')
          a.href = blobUrl
          const date = new Date(formData.cutoffDate).toISOString().split('T')[0]
          a.download = `reporte_corte_${date}.xlsx`
          document.body.appendChild(a)
          a.click()

          window.URL.revokeObjectURL(blobUrl)
          document.body.removeChild(a)

          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Reporte generado y descargado automáticamente.',
          })
          this.hideGenerateDialog()
          this.apiWalletConfigService.refreshData()
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'La respuesta no es un archivo Excel válido.',
          })
        }
      },
      error: (error) => {
        this.loadingGenerate = false
        // El error de blob se debe manejar de forma especial
        if (error.msg) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error al generar el reporte',
            detail: error.msg || 'Ocurrió un error desconocido.',
          })
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al conectar con el servidor',
          })
        }
      },
    })
  }

  loadStatistics(days = 30): void {
    this.loadingStats = true
    //* Los mismos tipos que la tabla de abajo: `corte-cartera` y el `null` de las entradas previas
    //* a F-098. Un encabezado y una tabla que cuentan cosas distintas es peor que no tener cifras.
    this.apiWalletConfigService.getStatistics(days, 'corte-cartera,null').subscribe({
      next: (response) => {
        this.loadingStats = false
        if (!response.error) {
          console.log('Statistics response:', response.data)
          this.statistics = response.data

          // Procesar distribución por tipo si está disponible
          if (this.statistics.typeDistribution && this.statistics.typeDistribution.length > 0) {
            this.processTypeDistribution()
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'Error al cargar estadísticas',
          })
        }
      },
      error: () => {
        this.loadingStats = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor',
        })
      },
    })
  }

  // Nuevo método para procesar la distribución por tipo
  processTypeDistribution(): void {
    const distribution = this.statistics.typeDistribution
    const automaticReports = distribution.find((item: any) => item._id === 'automatic')?.count || 0
    const manualReports = distribution.find((item: any) => item._id === 'manual')?.count || 0

    // Agregar información procesada al objeto statistics
    this.statistics.automaticReports = automaticReports
    this.statistics.manualReports = manualReports
    this.statistics.totalFromDistribution = automaticReports + manualReports
  }

  // Método para obtener el tipo de reporte más común
  getMostCommonReportType(): string {
    if (!this.statistics.typeDistribution || this.statistics.typeDistribution.length === 0) {
      return 'N/A'
    }

    const maxType = this.statistics.typeDistribution.reduce((prev: any, current: any) => (prev.count > current.count ? prev : current))

    return maxType._id === 'automatic' ? 'Automático' : 'Manual'
  }

  // Método para verificar si hay datos de estadísticas
  hasStatisticsData(): boolean {
    return this.statistics && (this.statistics.totalReports > 0 || this.statistics.successfulReports > 0 || this.statistics.failedReports > 0)
  }

  // Método para formatear la fecha del último reporte
  formatLastReportDate(): string {
    if (!this.statistics.lastReportDate || this.statistics.lastReportDate === '') {
      return 'N/A'
    }

    try {
      const date = new Date(this.statistics.lastReportDate)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return 'Fecha inválida'
    }
  }

  // Método para formatear el tiempo promedio de procesamiento
  formatAvgProcessingTime(): string {
    if (!this.statistics.avgProcessingTime || this.statistics.avgProcessingTime === '0.0') {
      return 'N/A'
    }
    return `${this.statistics.avgProcessingTime} min`
  }

  // Nuevo método para cargar configuraciones
  loadConfigurations(): void {
    this.loadingConfigurations = true

    // Cargar configuración principal
    this.apiWalletConfigService.getPrimaryConfiguration().subscribe({
      next: (response) => {
        if (!response.error && response.data) {
          this.primaryConfiguration = response.data as ICutoffConfiguration
        }
      },
      error: () => {
        console.error('Error al cargar configuración principal')
      },
    })

    // Cargar todas las configuraciones para obtener las adicionales
    const filterOptions = {
      page: 1,
      limit: 100, // Obtener todas las configuraciones
      filters: {},
    }

    this.apiWalletConfigService.getAllConfigurations(filterOptions).subscribe({
      next: (response) => {
        this.loadingConfigurations = false
        if (!response.error) {
          // Filtrar configuraciones adicionales (no principales)
          this.additionalConfigurations = response.data.data.filter((config: ICutoffConfiguration) => !config.isPrimary)

          // Ordenar configuraciones adicionales
          this.additionalConfigurations = this.walletConfigService.sortConfigurations(this.additionalConfigurations)
        }
      },
      error: () => {
        this.loadingConfigurations = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar configuraciones',
        })
      },
    })
  }

  hideGenerateDialog(): void {
    this.showGenerateDialog = false
    this.generateForm.reset()
    this.generateForm.patchValue({
      incluirDetalles: true,
    })
  }

  hideStatsDialog(): void {
    this.showStatsDialog = false
  }

  // Nuevo método para ocultar dialog de configuraciones
  hideConfigurationsDialog(): void {
    this.showConfigurationsDialog = false
  }

  downloadReport(url: string): void {
    window.open(url, '_blank')
  }

  // Manejar acciones de la tabla
  actionMethods(data: { event: any; action: string; data: any }): void {
    switch (data.action) {
      case 'VIEW':
        this.viewReportDetails(data.data)
        break
      case 'DOWNLOAD':
        this.downloadReportFromTable(data.data)
        break
      default:
        console.log('Acción no reconocida:', data.action)
        break
    }
  }

  downloadReportFromTable(reportData: any): void {
    // Validaciones previas según documentación
    if (!reportData) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No hay datos del reporte disponibles',
      })
      return
    }

    if (reportData.status !== 'success') {
      this.messageService.add({
        severity: 'warning',
        summary: 'Archivo no disponible',
        detail: 'Solo los reportes exitosos tienen archivos disponibles',
      })
      return
    }

    if (!reportData.fileUrl) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Sin archivo',
        detail: 'Este reporte no tiene archivo asociado',
      })
      return
    }

    // Verificar si ya se está descargando
    if (this.downloadingReports.has(reportData._id)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Descarga en progreso',
        detail: 'La descarga de este reporte ya está en proceso',
      })
      return
    }

    // Agregar a la lista de descargas en progreso
    this.downloadingReports.add(reportData._id)

    this.apiWalletConfigService.downloadReportFile(reportData._id).subscribe({
      next: (blob: Blob) => {
        this.downloadingReports.delete(reportData._id)

        // Generar nombre de archivo con fecha del reporte
        const reportDate = new Date(reportData.createdAt || reportData.generationDate)
        const dateStr = reportDate.toISOString().slice(0, 10) // YYYY-MM-DD
        const filename = `reporte-cartera-${dateStr}.csv`

        // Triggear descarga
        this.triggerFileDownload(blob, filename)

        this.messageService.add({
          severity: 'success',
          summary: 'Descarga exitosa',
          detail: 'El archivo se ha descargado correctamente',
        })
      },
      error: (error) => {
        this.downloadingReports.delete(reportData._id)
        this.handleDownloadError(error)
      },
    })
  }

  private triggerFileDownload(blob: Blob, filename: string): void {
    // Crear URL temporal para el blob
    const url = window.URL.createObjectURL(blob)

    // Crear elemento link temporal
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'

    // Agregar al DOM, hacer click y remover
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Cleanup URL temporal
    window.URL.revokeObjectURL(url)
  }

  private handleDownloadError(error: any): void {
    console.error('Download error:', error)

    // Manejo específico de errores según documentación
    if (error.status === 400) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Archivo no encontrado',
        detail: 'El reporte no tiene archivo asociado',
      })
    } else if (error.status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sin permisos',
        detail: 'No tienes permisos para descargar este archivo',
      })
    } else if (error.status === 401) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sesión expirada',
        detail: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente',
      })
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de descarga',
        detail: 'Error al descargar el archivo. Intenta nuevamente',
      })
    }
  }

  // Método auxiliar para verificar si un reporte está siendo descargado
  isDownloading(reportId: string): boolean {
    return this.downloadingReports.has(reportId)
  }

  viewReportDetails(report: any): void {
    this.walletConfigService.setSelectedReport(report)
    // Navegar a la página de detalles - necesitamos importar Router
    window.location.href = `/wallet-config/${report._id}`
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'success':
        return 'success'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'danger'
      default:
        return 'info'
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'success':
        return 'Exitoso'
      case 'pending':
        return 'Procesando'
      case 'failed':
        return 'Fallido'
      default:
        return 'Desconocido'
    }
  }

  // Nuevos métodos para manejar configuraciones
  getConfigStatusLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo'
  }

  getConfigStatusSeverity(isActive: boolean): string {
    return isActive ? 'success' : 'secondary'
  }

  cronToReadable(cronExpression: string): string {
    return this.walletConfigService.cronToReadable(cronExpression)
  }

  formatRecipientsList(recipients: string[]): string {
    return this.walletConfigService.formatRecipientsList(recipients, 2)
  }

  formatClientCount(clientIds: string[]): string {
    return this.walletConfigService.formatClientCount(clientIds)
  }

  // Manejar acciones de configuraciones
  configurationActions(action: string, config: ICutoffConfiguration): void {
    switch (action) {
      case 'EDIT':
        this.editConfiguration(config)
        break
      case 'DELETE':
        this.deleteConfiguration(config)
        break
      case 'TOGGLE':
        this.toggleConfiguration(config)
        break
      default:
        console.log('Acción de configuración no reconocida:', action)
        break
    }
  }

  // Cargar clientes disponibles
  loadAvailableClients(): void {
    this.loadingClients = true
    this.apiCustomerService.getClientNames().subscribe({
      next: (response) => {
        this.loadingClients = false
        if (!response.error) {
          // Transformar la respuesta al formato esperado por el componente
          this.availableClients = response.data.map((client) => ({
            id: client._id,
            name: client.name,
            label: client.name,
            value: client._id,
          }))
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'Error al cargar clientes',
          })
          // Mantener array vacío en caso de error
          this.availableClients = []
        }
      },
      error: () => {
        this.loadingClients = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor para cargar clientes',
        })
        this.availableClients = []
      },
    })
  }

  // Mostrar dialog de creación
  showCreateConfigDialog(): void {
    this.configFormMode = 'CREATE'
    this.editingConfiguration = null
    this.resetConfigForm()
    this.showConfigFormDialog = true
  }

  // Editar configuración existente
  editConfiguration(config: ICutoffConfiguration): void {
    this.configFormMode = 'EDIT'
    this.editingConfiguration = config
    this.populateConfigForm(config)
    this.showConfigFormDialog = true
  }

  // Resetear formulario de configuración
  resetConfigForm(): void {
    this.configForm.reset({
      name: '',
      description: '',
      cronExpression: '',
      cronPreset: '',
      recipients: '',
      clientIds: [],
      isActive: true,
      isPrimary: false,
    })
    this.showCustomCron = false
  }

  // Poblar formulario con datos existentes
  populateConfigForm(config: ICutoffConfiguration): void {
    this.configForm.patchValue({
      name: config.name,
      description: config.description || '',
      cronExpression: config.cronExpression,
      cronPreset: '', // Se podría buscar si coincide con algún preset
      recipients: config.recipients.join(', '),
      clientIds: config.clientIds,
      isActive: config.isActive,
      isPrimary: config.isPrimary,
    })

    // Mostrar campo personalizado si no coincide con presets
    const matchesPreset = this.cronPresets.some((preset) => preset.value === config.cronExpression)
    this.showCustomCron = !matchesPreset
  }

  // Manejar cambio de preset de cron
  onCronPresetChange(event: any): void {
    if (event.value) {
      this.configForm.patchValue({ cronExpression: event.value })
      this.showCustomCron = false
    }
  }

  // Obtener descripción legible de expresión cron
  getCronDescription(cronExpression: string): string {
    return this.walletConfigService.cronToReadable(cronExpression)
  }

  // Guardar configuración (crear o actualizar)
  saveConfiguration(): void {
    if (this.configForm.invalid) {
      this.markFormGroupTouched(this.configForm)
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos correctamente',
      })
      return
    }

    this.loadingConfigForm = true
    const formData = this.configForm.value

    // Convertir recipients string a array
    const recipientsArray = formData.recipients
      .split(',')
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0)

    if (this.configFormMode === 'CREATE') {
      this.createConfiguration(formData, recipientsArray)
    } else {
      this.updateConfiguration(formData, recipientsArray)
    }
  }

  // Crear nueva configuración
  createConfiguration(formData: any, recipientsArray: string[]): void {
    const createData: ICreateCutoffConfigurationDto = {
      name: formData.name,
      cronExpression: formData.cronExpression,
      recipients: recipientsArray,
      clientIds: formData.clientIds || [],
      isActive: formData.isActive,
      isPrimary: formData.isPrimary,
      description: formData.description || undefined,
    }

    this.apiWalletConfigService.createConfiguration(createData).subscribe({
      next: (response) => {
        this.loadingConfigForm = false
        if (!response.error) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Configuración creada exitosamente',
          })
          this.hideConfigFormDialog()
          this.loadConfigurations() // Recargar configuraciones
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'Error al crear la configuración',
          })
        }
      },
      error: () => {
        this.loadingConfigForm = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor',
        })
      },
    })
  }

  // Actualizar configuración existente
  updateConfiguration(formData: any, recipientsArray: string[]): void {
    if (!this.editingConfiguration) return

    const updateData: IUpdateCutoffConfigurationDto = {
      name: formData.name,
      cronExpression: formData.cronExpression,
      recipients: recipientsArray,
      clientIds: formData.clientIds || [],
      isActive: formData.isActive,
      description: formData.description || undefined,
    }

    // No permitir cambiar isPrimary en edición
    if (formData.isPrimary !== this.editingConfiguration.isPrimary) updateData.isPrimary = formData.isPrimary

    this.apiWalletConfigService.updateConfiguration(this.editingConfiguration._id, updateData).subscribe({
      next: (response) => {
        this.loadingConfigForm = false
        if (!response.error) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Configuración actualizada exitosamente',
          })
          this.hideConfigFormDialog()
          this.loadConfigurations() // Recargar configuraciones
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'Error al actualizar la configuración',
          })
        }
      },
      error: () => {
        this.loadingConfigForm = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor',
        })
      },
    })
  }

  // Ocultar dialog de formulario
  hideConfigFormDialog(): void {
    this.showConfigFormDialog = false
    this.resetConfigForm()
    this.editingConfiguration = null
  }

  // Marcar todos los campos del formulario como touched
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key)
      control?.markAsTouched()
    })
  }

  deleteConfiguration(config: ICutoffConfiguration): void {
    if (config.isPrimary) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede eliminar la configuración principal',
      })
      return
    }

    // Confirmar eliminación con p-confirmDialog
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar la configuración de corte <strong>"${config.name}"</strong>?<br><br>Esta acción no se puede deshacer.`,
      header: 'Confirmar eliminación',
      icon: 'fa-solid fa-triangle-exclamation',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        // Ejecutar eliminación
        this.apiWalletConfigService.deleteConfiguration(config._id).subscribe({
          next: (response) => {
            // Manejar respuesta null o undefined (eliminación exitosa)
            if (response === null || response === undefined || !response?.error) {
              this.messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: `La configuración "${config.name}" fue eliminada correctamente`,
              })

              // Recargar configuraciones para actualizar la lista
              this.loadConfigurations()
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: response.msg || 'Error al eliminar la configuración',
              })
            }
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al conectar con el servidor',
            })
          },
        })
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelado',
          detail: 'La configuración no fue eliminada',
        })
      },
    })
  }

  toggleConfiguration(config: ICutoffConfiguration): void {
    this.apiWalletConfigService.toggleConfiguration(config._id).subscribe({
      next: (response) => {
        if (!response.error) {
          const newStatus = !config.isActive
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `Configuración ${newStatus ? 'activada' : 'desactivada'} correctamente`,
          })
          // Recargar configuraciones
          this.loadConfigurations()
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'Error al cambiar el estado de la configuración',
          })
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor',
        })
      },
    })
  }
}
